import { QueryKey, useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect } from 'react';

import {
  useAddRequireLoading,
  useRemoveRequireLoading,
} from '../components/modules/next-loading';
import UniqueVehiclePage from '../components/pages/unique-vehicle';
import { useAppContext } from '../components/providers/app-context';
import { DETAILED_PRICES, SHIPPING_LOCATION } from '../static/cookies';
import { isGlobalUser } from '../utils/is-global-user';
import { isUKuser } from '../utils/is-uk-user';
import { isUSuser } from '../utils/is-us-user';
import getGroups from '../utils/react-query/query-options/get-groups';
import getSimilarVehicles from '../utils/react-query/query-options/get-similar-vehicles';
import getUniqueVehicle from '../utils/react-query/query-options/get-unique-vehicle';
import { kmsToMiles } from '../utils/kms-to-miles';
import forceShippingUKForCoUK from '../utils/force-shipping-uk-for-co-uk';
import { getSortedGroups, getSortedProductsPerGroups } from '../utils/get-sorted-groups';

const useData = () => {
  const {
    locale = 'en',
    query: { vehicle = '' },
  } = useRouter();
  const { detailedPrice } = useAppContext();

  const { data: groups, isFetching: isgroupsLoading } = useQuery(
    ...getGroups({ locale }),
    {
      keepPreviousData: true,
    }
  );

  let groupsSort: TCategories = [];
  if (groups) {
    groupsSort = getSortedGroups(groups);
  }

  const { data: uniqueVehicle, isFetching: isProductDetailsLoading } = useQuery<
    unknown,
    unknown,
    TVehicle,
    QueryKey
  >(
    ...getUniqueVehicle({
      locale,
      vehicle: vehicle as string,
      detailed_prices: !!detailedPrice,
    }),
    {
      keepPreviousData: true,
    }
  );

  if (uniqueVehicle && process.env.NEXT_PUBLIC_HOST?.includes('.co.uk')) {
    if (uniqueVehicle.mileage) {
      let productMileage =
        typeof uniqueVehicle.mileage === 'string'
          ? parseInt(uniqueVehicle.mileage, 10)
          : uniqueVehicle.mileage;
      uniqueVehicle.milageCoUK = kmsToMiles(productMileage);
    }
  }

  const { data: similarVehicles, isFetching: isRelatedProductsLoading } =
    useQuery(...getSimilarVehicles({ locale, vehicle: vehicle as string }), {
      keepPreviousData: true,
    });

  return {
    groups: groupsSort || groups,
    uniqueVehicle,
    similarVehicles,
    isLoading:
      isProductDetailsLoading || isRelatedProductsLoading || isgroupsLoading,
  };
};

const UniqueVehicle: FunctionComponent<JSX.Element> & TPageConfig = () => {
  const data = useData();
  const addLoading = useAddRequireLoading();
  const removeLoading = useRemoveRequireLoading();

  useEffect(() => {
    if (data.isLoading) {
      addLoading('vehicle');
    } else {
      removeLoading('vehicle');
    }
  }, [addLoading, removeLoading, data.isLoading]);

  return (
    <main className={cx({ 'opacity-75': data.isLoading })}>
      <UniqueVehiclePage {...data} />
    </main>
  );
};

export default UniqueVehicle;

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query: { vehicle },
  params,
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });

  const { default: getGenericClient } = await import('../server/client');
  const genericClient = getGenericClient();
  const { default: slugChange301 } = await import('../utils/slug-change-301');

  try {
    const [groups, uniqueVehicle] = await Promise.all([
      genericClient.fetchQuery(
        ...getGroups({ locale }, req),
      ),
      genericClient.fetchQuery(
        ...getUniqueVehicle(
          {
            locale,
            vehicle: vehicle as string,
            detailed_prices: req.cookies[DETAILED_PRICES] === 'true',
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getSimilarVehicles({ locale, vehicle: vehicle as string }, req)
      ),
    ]);

    const { default: retrieveStateAndClear } = await import(
      '../utils/retrieve-state-and-clear'
    );

    let groupsSort: TCategories = [];
    if (groups) {
      groupsSort = getSortedGroups(groups);
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
        groupsSort,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...params,
        ...(e.data.vehicle && { vehicle: e.data.vehicle }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: 'vehicle',
      });
    } else if (e.status === 404) {
      return { notFound: true };
    }
    console.log(e);
    throw e;
  }
};
